import React from 'react';
export type Language = 'en' | 'pt';
export interface Translations {
  // Navigation
  nav: {
    home: string;
    browse: string;
    services: string;
    listProperty: string;
    dashboard: string;
    login: string;
    admin: string;
  };
  // Hero Section
  hero: {
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    searchButton: string;
  };
  // Features
  features: {
    title: string;
    subtitle: string;
    verified: {
      title: string;
      description: string;
    };
    community: {
      title: string;
      description: string;
    };
    insights: {
      title: string;
      description: string;
    };
  };
  // Services
  services: {
    title: string;
    subtitle: string;
    photography: {
      title: string;
      description: string;
      learnMore: string;
    };
    valuation: {
      title: string;
      description: string;
      learnMore: string;
    };
  };
  // Property Types
  propertyTypes: {
    title: string;
    houses: string;
    land: string;
    explore: string;
  };
  // Featured Properties
  featured: {
    title: string;
    viewAll: string;
  };
  // CTA
  cta: {
    title: string;
    subtitle: string;
    button: string;
  };
  // Footer
  footer: {
    description: string;
    explore: string;
    services: string;
    contact: string;
    privacyPolicy: string;
    termsOfService: string;
    cookiePolicy: string;
    allRightsReserved: string;
  };
  // Common
  common: {
    forSale: string;
    forRent: string;
    featured: string;
    beds: string;
    baths: string;
    pending: string;
    approved: string;
    rejected: string;
    active: string;
    inactive: string;
  };
}
export const translations: Record<Language, Translations> = {
  pt: {
    nav: {
      home: 'Início',
      browse: 'Explorar Imóveis',
      services: 'Serviços',
      listProperty: 'Anunciar Imóvel',
      dashboard: 'Painel',
      login: 'Entrar',
      admin: 'Administração'
    },
    hero: {
      title: 'Encontre Seu Imóvel Perfeito com Confiança',
      subtitle: 'Plataforma confiável para comprar, vender e alugar imóveis',
      searchPlaceholder: 'Pesquisar por localização ou tipo de imóvel...',
      searchButton: 'Pesquisar Imóveis'
    },
    features: {
      title: 'Por Que Escolher a Kredita',
      subtitle: 'Construindo confiança e credibilidade em transações imobiliárias',
      verified: {
        title: 'Anúncios Verificados',
        description: 'Todos os imóveis são verificados pela nossa equipe de suporte com documentação adequada antes de serem publicados'
      },
      community: {
        title: 'Comunidade Confiável',
        description: 'Conecte-se com compradores, vendedores e locatários verificados em um ambiente seguro'
      },
      insights: {
        title: 'Análise de Mercado',
        description: 'Acesse dados de mercado em tempo real e avaliações de imóveis para tomar decisões informadas'
      }
    },
    services: {
      title: 'Serviços Profissionais',
      subtitle: 'Melhore seus anúncios com nossa fotografia especializada e avaliações certificadas de imóveis',
      photography: {
        title: 'Fotografia Profissional',
        description: 'Mostre seu imóvel com fotos impressionantes e de alta qualidade. Fotos aéreas com drone, tours virtuais e entrega rápida.',
        learnMore: 'Saiba mais →'
      },
      valuation: {
        title: 'Avaliação de Imóveis',
        description: 'Obtenha avaliações certificadas de profissionais licenciados. Legalmente conforme e aceito por todas as instituições.',
        learnMore: 'Saiba mais →'
      }
    },
    propertyTypes: {
      title: 'Explorar Imóveis',
      houses: 'Casas',
      land: 'Terrenos',
      explore: 'Explorar →'
    },
    featured: {
      title: 'Imóveis em Destaque',
      viewAll: 'Ver Todos os Imóveis'
    },
    cta: {
      title: 'Pronto para Anunciar Seu Imóvel?',
      subtitle: 'Junte-se à Kredita e mostre seu imóvel para milhares de compradores e locatários verificados',
      button: 'Anunciar Seu Imóvel'
    },
    footer: {
      description: 'Sua plataforma confiável para comprar, vender e alugar imóveis. Construindo credibilidade no mercado imobiliário.',
      explore: 'Explorar',
      services: 'Serviços',
      contact: 'Contato',
      privacyPolicy: 'Política de Privacidade',
      termsOfService: 'Termos de Serviço',
      cookiePolicy: 'Política de Cookies',
      allRightsReserved: 'Todos os direitos reservados.'
    },
    common: {
      forSale: 'Venda',
      forRent: 'Aluguel',
      featured: 'Destaque',
      beds: 'Quartos',
      baths: 'Banheiros',
      pending: 'Pendente',
      approved: 'Aprovado',
      rejected: 'Rejeitado',
      active: 'Ativo',
      inactive: 'Inativo'
    }
  },
  en: {
    nav: {
      home: 'Home',
      browse: 'Browse Properties',
      services: 'Services',
      listProperty: 'List Property',
      dashboard: 'Dashboard',
      login: 'Login',
      admin: 'Admin'
    },
    hero: {
      title: 'Find Your Perfect Property with Confidence',
      subtitle: 'Trusted platform for buying, selling, and renting properties',
      searchPlaceholder: 'Search by location or property type...',
      searchButton: 'Search Properties'
    },
    features: {
      title: 'Why Choose Kredita',
      subtitle: 'Building trust and credibility in real estate transactions',
      verified: {
        title: 'Verified Listings',
        description: 'All properties are verified by our desk support team with proper documentation before going live'
      },
      community: {
        title: 'Trusted Community',
        description: 'Connect with verified buyers, sellers, and renters in a secure environment'
      },
      insights: {
        title: 'Market Insights',
        description: 'Access real-time market data and property valuations to make informed decisions'
      }
    },
    services: {
      title: 'Professional Services',
      subtitle: 'Enhance your listings with our expert photography and certified property valuations',
      photography: {
        title: 'Professional Photography',
        description: 'Showcase your property with stunning, high-quality photos. Drone shots, virtual tours, and fast turnaround.',
        learnMore: 'Learn more →'
      },
      valuation: {
        title: 'Property Valuation',
        description: 'Get certified valuations from licensed professionals. Legally compliant and accepted by all institutions.',
        learnMore: 'Learn more →'
      }
    },
    propertyTypes: {
      title: 'Explore Properties',
      houses: 'Houses',
      land: 'Land',
      explore: 'Explore →'
    },
    featured: {
      title: 'Featured Properties',
      viewAll: 'View All Properties'
    },
    cta: {
      title: 'Ready to List Your Property?',
      subtitle: 'Join Kredita and showcase your property to thousands of verified buyers and renters',
      button: 'List Your Property'
    },
    footer: {
      description: 'Your trusted platform for buying, selling, and renting properties. Building credibility in real estate.',
      explore: 'Explore',
      services: 'Services',
      contact: 'Contact Us',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      cookiePolicy: 'Cookie Policy',
      allRightsReserved: 'All rights reserved.'
    },
    common: {
      forSale: 'For Sale',
      forRent: 'For Rent',
      featured: 'Featured',
      beds: 'Beds',
      baths: 'Baths',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      active: 'Active',
      inactive: 'Inactive'
    }
  }
};
export const getTranslation = (language: Language): Translations => {
  return translations[language];
};